import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { makeStyles } from '@material-ui/core/styles';
import { Color } from '@material-ui/lab/Alert/Alert';
import React from 'react';
import { useIntl } from 'react-intl';
import { Account } from '../hooks/useAccount';
import { Person, Product } from '../swagger';
import { ageOrPnoToAge } from '../utils/ageOrPersonalNumber';
import { parseQuiz } from '../utils/parseQuiz';
import { InsuranceProductStatusWrapper } from './InsuranceProductStatusWrapper';
import { Markdown } from './Markdown';
import TypeEnum = Person.TypeEnum;

const styles = makeStyles({
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',

    '& button': {
      margin: '8px 0',
      marginRight: '8px',

      '&:last-of-type': {
        marginRight: 0,
      },
    },
  },
});

export enum Status {
  EMPTY = 'empty',
  IN_PROGRESS = 'in_progress',
  PENDING = 'pending',
  SUCCESS = 'success',
  ADULT18 = 'adult18',
  ADULT65 = 'adult65',
  CHILD18 = 'child18',
  ERROR = 'error',
  HEALTH_CHECKUP = 'health_checkup',
}

const severityMap: Record<Status, Color> = {
  [Status.EMPTY]: 'info',
  [Status.IN_PROGRESS]: 'info',
  [Status.PENDING]: 'success',
  [Status.SUCCESS]: 'success',
  [Status.CHILD18]: 'error',
  [Status.ADULT18]: 'error',
  [Status.ADULT65]: 'error',
  [Status.ERROR]: 'error',
  [Status.HEALTH_CHECKUP]: 'info',
};

export const getStatus = (
  person: Person,
  isHealthCheckup?: boolean,
  isAdmin?: boolean,
): Status => {
  if (!person.quiz) {
    return Status.EMPTY;
  }

  const { isDone, hasProducts } = parseQuiz(person.quiz);
  const age = parseInt(ageOrPnoToAge(person.personnummer), 10);

  if (!isHealthCheckup && isDone && !hasProducts) {
    return Status.ERROR;
  }

  if (Number.isNaN(age)) {
    return Status.IN_PROGRESS;
  } else {
    if (
      person.type === 'spouse' &&
      person.email &&
      typeof person.quiz.age !== 'number'
    ) {
      return Status.PENDING;
    }
    if (person.type === 'child' && age >= 18 && !isAdmin) {
      return Status.CHILD18;
    }
    if (person.type !== 'child' && age >= 65) {
      return Status.ADULT65;
    }
    if (person.type !== 'child' && age < 18) {
      return Status.ADULT18;
    }
  }

  if (isDone && hasProducts) {
    return Status.SUCCESS;
  }

  return Status.IN_PROGRESS;
};

type Props = {
  person: Person;
  products?: Product[];
  isBroker?: boolean;
  isOrder?: boolean;
  /** The invited person's own page (`/quiz/:id`): no price is ever shown there. */
  isInvite?: boolean;
  /**
   * A parent answering for their child: `person` is the parent who signs,
   * the declaration is the child's. No adult age limits on the parent (a
   * 65+ parent may sign), and the wording is about the child.
   */
  forChild?: boolean;
  isHealthCheckup?: boolean;
  children?: (status: Status) => React.ReactElement;
};

export const InsuranceProductStatus: React.FC<Props> = ({
  person,
  products: orderProducts,
  children,
  isOrder,
  isBroker,
  isInvite,
  isHealthCheckup,
  forChild,
}) => {
  const { actions } = styles();
  const { admin, iss } = Account.useContainer();
  const [status, setStatus] = React.useState(Status.EMPTY);
  const quiz = !isHealthCheckup ? person.quiz : undefined;
  const parsedQuiz = quiz && parseQuiz(quiz);
  const productTypes =
    parsedQuiz?.products || orderProducts?.map((pr) => pr.type) || [];
  const product: Product.TypeEnum | undefined = productTypes[0];
  const availableTreatments =
    quiz?.availableTreatments || orderProducts?.[0]?.availableTreatments || [];
  const unavailableTreatments =
    quiz?.unavailableTreatments ||
    orderProducts?.[0]?.unavailableTreatments ||
    [];
  const { messages } = useIntl();
  const labelPrefix = isHealthCheckup ? 'checkup_status' : 'insurance_status';
  const possibleLabels = [
    ...(isInvite && forChild ? [`${labelPrefix}.${status}.child.invite`] : []),
    ...(isInvite ? [`${labelPrefix}.${status}.invite`] : []),
    ...(isOrder
      ? [
          `${labelPrefix}.${status}.${person.type}.order`,
          `${labelPrefix}.${status}.order`,
        ]
      : []),
    ...(isBroker
      ? [
          `${labelPrefix}.${status}.${person.type}.broker`,
          `${labelPrefix}.${status}.broker`,
        ]
      : []),
    `${labelPrefix}.${status}.${person.type}`,
    `${labelPrefix}.${status}`,
  ];

  const statusLabel = possibleLabels.find((label) => label in messages);
  const hasProducts = !!orderProducts?.length;

  React.useEffect(() => {
    if (isHealthCheckup && hasProducts) {
      setStatus(Status.SUCCESS);
    } else {
      setStatus(
        getStatus(
          {
            ...person,
            type: forChild
              ? TypeEnum.Child
              : person.type === TypeEnum.Spouse && admin
              ? TypeEnum.Main
              : person.type,
          },
          isHealthCheckup,
          // A child's declaration signed by an adult: the "child over 18"
          // rule would read the parent's age, as the admin's override does.
          admin || iss === 'admin' || !!forChild,
        ),
      );
    }
    // Keyed on the person fields the status actually reads, not on the
    // object identity, which changes on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quiz,
    person.personnummer,
    person.type,
    admin,
    isHealthCheckup,
    hasProducts,
    iss,
    forChild,
  ]);

  return (
    <InsuranceProductStatusWrapper severity={severityMap[status]}>
      {status !== Status.SUCCESS && statusLabel && (
        <Markdown children={statusLabel} variant="body2" />
      )}
      {status === Status.SUCCESS && product && (
        <>
          <Markdown children={`product.${product}.title`} variant="h6" />
          {!!availableTreatments.length && (
            <List>
              {availableTreatments.map((treatment) => (
                <ListItem className="yes" key={treatment}>
                  {messages[`treatment.${treatment}`] ||
                    `treatment.${treatment}`}
                </ListItem>
              ))}
            </List>
          )}
          {!!unavailableTreatments.length && (
            <List>
              {unavailableTreatments.map((treatment) => (
                <ListItem className="no" key={treatment}>
                  {messages[`treatment.${treatment}`] ||
                    `treatment.${treatment}`}
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}
      {/* A missing message shows its ids, so the gap is seen — except on
          success, which has no message by design: the product says it all. */}
      {!statusLabel &&
        status !== Status.SUCCESS &&
        possibleLabels.map((label) => <Box key={label}>{label}</Box>)}
      {children && <Box className={actions}>{children(status)}</Box>}
    </InsuranceProductStatusWrapper>
  );
};
